import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { CtaBand } from "@/components/CtaBand";
import { WordReveal } from "@/components/WordReveal";
import { GalleryTile } from "@/components/GalleryTile";
import { GalleryPlayerModal } from "@/components/GalleryPlayerModal";
import { VIDEO, IMG } from "@/lib/images";
import { getJSON } from "@/lib/api";

export default function Gallery() {
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("All");
  const triggerRef = useRef(null);

  const openPlayer = (item, e) => {
    triggerRef.current = e.currentTarget;
    setActive(item);
  };

  const { data: items = [] } = useQuery({
    queryKey: ["gallery"],
    queryFn: () => getJSON("/gallery"),
  });

  const FILTERS = ["All", "Long-form", "Talking heads", "Advertisements"];
  const visible =
    filter === "All"
      ? items
      : items.filter((it) => (it.content_types || []).includes(filter));

  return (
    <>
      {/* Hero */}
      <section className="relative flex min-h-[100svh] w-full overflow-hidden" data-testid="gallery-hero">
        <div className="absolute inset-0">
          <video
            className="h-full w-full object-cover"
            src={VIDEO.creative}
            poster={IMG.creativeHero}
            muted
            loop
            autoPlay
            playsInline
            preload="metadata"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#141210] via-[#141210]/60 to-[#141210]/45" />
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col justify-end px-6 pb-20 pt-36 md:px-12 md:pb-28">
          <div className="flex max-w-3xl flex-col gap-6">
            <span
              className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--mint)] opacity-0"
              style={{ animation: "wordIn .7s var(--ease) .15s forwards" }}
            >
              The Reel — Video Gallery
            </span>
            <h1 className="t-hero text-white">
              <WordReveal text="Work that doesn't look AI-made." start={300} step={85} />
            </h1>
            <p
              className="max-w-xl text-base leading-relaxed text-white/80 sm:text-lg opacity-0"
              style={{ animation: "wordIn .8s var(--ease) .9s forwards" }}
            >
              Ad creative, motion, and AI avatar video — produced at scale,
              directed by a human with taste. Press play on any of it.
            </p>
          </div>
        </div>

        <a
          href="#reel"
          className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 text-white/70 transition-colors hover:text-white md:block"
          aria-label="Scroll to the reel"
        >
          <ChevronDown size={26} className="animate-bounce" />
        </a>
      </section>

      {/* The reel grid */}
      <section id="reel" className="scroll-mt-24 bg-[var(--bg-base)] py-20 md:py-28" data-testid="gallery-grid-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          {/* Content-type filter */}
          <div className="mb-10 flex flex-wrap gap-2.5" data-testid="gallery-filter">
            {FILTERS.map((cat) => {
              const on = filter === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors duration-200 ${on ? "border-[var(--mint)] bg-[var(--mint)] text-[var(--charcoal)]" : "border-black/15 bg-transparent text-[var(--ink)]/70 hover:border-black/40 hover:text-[var(--ink)]"}`}
                  data-testid={`gallery-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div
            className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8"
            data-testid="gallery-grid"
          >
            {visible.map((item, i) => (
              <GalleryTile key={`${filter}-${item.id}`} item={item} onOpen={openPlayer} delay={(i % 3) * 100} />
            ))}
          </div>
          {visible.length === 0 && (
            <p className="py-10 text-center text-[var(--muted)]" data-testid="gallery-empty">
              {items.length === 0 ? "The reel is being cut. Check back soon." : "No videos in this category yet."}
            </p>
          )}
        </div>
      </section>

      <CtaBand />

      <GalleryPlayerModal
        open={!!active}
        item={active}
        triggerRef={triggerRef}
        onClose={() => setActive(null)}
      />
    </>
  );
}
